import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import { User, Sparkles, Check } from 'lucide-react';

export default function HerFacilitatorsPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [facilitators, setFacilitators] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', title: '', style: '', expertise: '', summary_text: '' });

  const canManage = ['admin', 'coordinator'].includes(user.role);
  const isAdmin = user.role === 'admin';

  const load = () => {
    api.get('/her/facilitators').then(r => setFacilitators(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.name || !form.summary_text) return toast.error('Name and summary are required');
    try {
      await api.post('/her/facilitators', form);
      toast.success('Facilitator submitted');
      setOpen(false);
      setForm({ name: '', title: '', style: '', expertise: '', summary_text: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to save facilitator');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/her/facilitators/${id}/approve`);
      toast.success('Facilitator approved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to approve');
    }
  };

  const approved = facilitators.filter(f => f.status === 'approved');
  const pending = facilitators.filter(f => f.status === 'pending');

  return (
    <div className="space-y-6" data-testid="her-facilitators-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>HER Facilitator Directory</h1>
          <p className="text-muted-foreground text-sm">Brief, professional bios for referral sources and participants.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full" data-testid="her-add-facilitator">
                Add Facilitator
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-xl">
              <DialogHeader><DialogTitle>Add Facilitator Bio</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Facilitator name" data-testid="her-facilitator-name" />
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title or role" data-testid="her-facilitator-title" />
                <Input value={form.style} onChange={e => setForm({ ...form, style: e.target.value })} placeholder="Teaching style (brief)" data-testid="her-facilitator-style" />
                <Input value={form.expertise} onChange={e => setForm({ ...form, expertise: e.target.value })} placeholder="Expertise (comma separated)" data-testid="her-facilitator-expertise" />
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Speech-to-text only (3 minute summary). AI will refine for professional tone.</p>
                  <DictationInput
                    value={form.summary_text}
                    onChange={(cleaned) => setForm({ ...form, summary_text: cleaned })}
                    rows={5}
                    placeholder="Speak: who you are, teaching style, what participants can expect..."
                    promptContext={{
                      context: 'her_facilitator',
                      taskName: form.name || 'Facilitator bio',
                      taskDescription: 'Facilitator introduction for HER',
                      category: 'heat'
                    }}
                    data-testid="her-facilitator-summary"
                  />
                </div>
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full" onClick={handleCreate} data-testid="her-facilitator-submit">
                  Submit Bio
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {pending.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400" data-testid="her-pending-facilitators">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Pending Approval</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {pending.map(f => (
              <div key={f.id} className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between" data-testid={`her-pending-${f.id}`}>
                <div>
                  <p className="text-sm font-semibold">{f.name}</p>
                  <p className="text-xs text-muted-foreground">{f.title}</p>
                </div>
                {isAdmin && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1" onClick={() => handleApprove(f.id)} data-testid={`her-approve-${f.id}`}>
                    <Check size={12} /> Approve
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {approved.map(f => (
          <Card key={f.id} className="border-0 shadow-sm rounded-2xl" data-testid={`her-facilitator-${f.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <User className="w-4 h-4 text-orange-600" /> {f.name}
              </CardTitle>
              <div className="flex gap-2 flex-wrap">
                {f.title && <Badge variant="secondary" className="bg-orange-50 text-orange-700">{f.title}</Badge>}
                {f.style && <Badge variant="secondary" className="bg-sky-50 text-sky-700">{f.style}</Badge>}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {f.expertise && <p className="text-xs text-muted-foreground">Expertise: {f.expertise}</p>}
              <div className="rounded-xl bg-muted/40 p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Bio (30 sec read)</p>
                <p className="text-sm text-slate-700">{f.summary_refined || f.summary_original}</p>
              </div>
              {f.status === 'pending' && <Badge className="bg-amber-50 text-amber-700">Pending Approval</Badge>}
              {f.status === 'approved' && <Badge className="bg-emerald-50 text-emerald-700">Approved</Badge>}
            </CardContent>
          </Card>
        ))}
      </div>

      {approved.length === 0 && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-6 text-center">
            <Sparkles className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No approved facilitators yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
